/**
 * KNK-1.55 — reading and changing an Account's add-on modules. Superadmin only, every call.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the answers mean is `lib/accountModules.ts`; who may change
 * what is the database's (migration 20261009a8). Errors are thrown with the server's own message.
 */

import { supabase } from '@/lib/supabaseClient';
import { parseMyAccountModules, type AccountModuleStatus } from '@/lib/accountFeatures';
import {
  parseAccountModules,
  parseModuleUsage,
  type AccountModule,
  type AccountModuleKey,
  type ModuleState,
  type ModuleUsageMonth,
} from '@/lib/accountModules';

export async function fetchAccountModules(tenantId: string): Promise<AccountModule[]> {
  const { data, error } = await supabase.rpc('account_modules_for', { p_tenant_id: tenantId });
  if (error) throw new Error(error.message);
  return parseAccountModules(data);
}

/** `trialEndsOn` is YYYY-MM-DD, required for a trial and refused otherwise. */
export async function setAccountModule(
  tenantId: string,
  module: AccountModuleKey,
  state: ModuleState,
  trialEndsOn: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_account_module', {
    p_tenant_id: tenantId,
    p_module: module,
    p_state: state,
    p_trial_ends_on: state === 'trial' ? trialEndsOn : null,
  });
  if (error) throw new Error(error.message);
}

export async function fetchAccountModuleUsage(tenantId: string, months = 6): Promise<ModuleUsageMonth[]> {
  const { data, error } = await supabase.rpc('account_module_usage', { p_tenant_id: tenantId, p_months: months });
  if (error) throw new Error(error.message);
  return parseModuleUsage(data);
}

/**
 * RBAC-6.1. Every module for the caller's CURRENT account, with whether it is active. Any signed-in
 * user may ask; it takes no id, so it cannot be pointed at another account (`my_account_modules()`,
 * migration 20261102b8). This is what decides whether a feature exists at all for the people in it.
 */
export async function fetchMyAccountModules(): Promise<AccountModuleStatus[]> {
  const { data, error } = await supabase.rpc('my_account_modules');
  if (error) throw new Error(error.message);
  return parseMyAccountModules(data);
}
